// Shared helpers for the negotiation detail page.

export function toUSD(money) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(money);
}

export function toUSDShort(money) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(money);
}

export function fmtDateTime(value) {
  return value ? new Date(value).toLocaleString() : "-";
}

// One look per side. Blue/orange differ in lightness as well as hue, and the chart
// also uses different marker shapes, so colour is never the only cue.
// Literal class names so Tailwind can see them.
export const SIDE_STYLE = {
  PLAINTIFF: {
    label: "Plaintiff",
    hex: "#2f6fd6",
    bubble: "border-[#2f6fd666] bg-[#eaf1fc]",
    head: "text-[#1f4fa3]",
    dot: "bg-[#2f6fd6]",
  },
  DEFENDANT: {
    label: "Defendant",
    hex: "#e07b39",
    bubble: "border-[#e07b3966] bg-[#fdf0e6]",
    head: "text-[#a8470f]",
    dot: "bg-[#e07b39]",
  },
  A: {
    label: "",
    hex: "#2f6fd6",
    bubble: "border-[#2f6fd666] bg-[#eaf1fc]",
    head: "text-[#1f4fa3]",
    dot: "bg-[#2f6fd6]",
  },
  B: {
    label: "",
    hex: "#e07b39",
    bubble: "border-[#e07b3966] bg-[#fdf0e6]",
    head: "text-[#a8470f]",
    dot: "bg-[#e07b39]",
  },
  NEUTRAL: {
    label: "Neutral",
    hex: "#7a869a",
    bubble: "border-[#7a869a66] bg-[#f1f3f6]",
    head: "text-[#4b5565]",
    dot: "bg-[#7a869a]",
  },
};

export const BASIS_TEXT = {
  BLIND: "Agreed through confidential bidding",
  PROPOSAL: "Agreed on the arbitrator's proposal",
  OFFER: "Agreed on an offer",
};

// Opening offer + counter-offers as a flat list with a side for each author. The API reports each author's
// real side (creator_side / author_side); only when it doesn't (a neutral author such as an admin) do we fall
// back to guessing: for a party viewer anyone else is the other side, for a neutral viewer authors alternate A / B.
export function buildOfferEntries(neg, details, viewer, userId) {
  const ownSide = viewer?.side || null;
  const order = [];
  const sideOf = (creatorId) => {
    if (ownSide) {
      if (creatorId === userId) return ownSide;
      return ownSide === "PLAINTIFF" ? "DEFENDANT" : "PLAINTIFF";
    }
    let i = order.indexOf(creatorId);
    if (i < 0) {
      order.push(creatorId);
      i = order.length - 1;
    }
    return i === 0 ? "A" : i === 1 ? "B" : "NEUTRAL";
  };
  const make = (e) => {
    const known = e.known_side || null;
    const side = known || sideOf(e.creator_id);
    return {
      ...e,
      side,
      right: known ? (ownSide ? known === ownSide : known === "DEFENDANT") : ownSide ? e.creator_id === userId : side === "B",
    };
  };
  const entries = [
    make({
      key: `open-${neg.id}`,
      opening: true,
      creator_id: neg.creator_id,
      known_side: neg.creator_side,
      creator_name: neg.creator_name,
      amount: neg.start_amount,
      text: null, // the description is shown in the page header
      terms: neg.terms,
      items: neg.terms_items || [],
      created_at: neg.created_at,
      withdrawn_at: null,
    }),
    ...details.map((d) => make({ key: `d-${d.id}`, opening: false, known_side: d.author_side, items: d.terms_items || [], ...d })),
  ];
  // a restarted negotiation has a withdrawn opening that is older than the current opening offer
  return entries.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}
